import React, { memo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, MapPin, Sparkles, Check, Eye } from 'lucide-react';
import { getBrazilianStatus } from '../../../../../utils/detectBrazil';
import { nflDashPtBR } from '../../../../../components/mappings/sports-dash-mappings/nfl-dash.pt-br';
import { useAddToChat } from '../../../../../components/chat/chatContext';
import XPicksButton from "../../../../../components/dashboards/components/shared/XPicksButton";
import PredictButton from "../../../../../components/dashboards/components/shared/PredictButton";
import ViewOddsButton from "../../../../../components/dashboards/components/shared/ViewOddsButton";
import PredictionBox from "../../../../../components/dashboards/components/shared/PredictionBox";

const NFLMobileGameCard = memo(({
  game,
  onViewOdds,
  isSelected = false,
  isFrozen = false,
  onToggleSelection,
  onViewPrediction,
  prediction = null,
  showPredictionButton = true
}) => {
  // Internationalization
  const isBrazilian = getBrazilianStatus();
  const t = (text) => isBrazilian ? (nflDashPtBR[text] || text) : text;
  const navigate = useNavigate();
  const handleAddToChat = useAddToChat();

  // Handler to stage prediction and navigate to chat
  const handleAnalyzeInChat = async () => {
    if (!prediction || !game?.teams) return;

    const message = `${t('Analyze this NFL prediction:')}

${game.teams?.away?.name} @ ${game.teams?.home?.name}

${t('Prediction:')} ${prediction.winner}${prediction.confidenceNumeric ? ` (${prediction.confidenceNumeric}% ${t('confidence')})` : ''}
${t('Confidence Level:')} ${prediction.confidence}
${prediction.reason ? `${t('Reasoning:')} ${prediction.reason}` : ''}

${t('Can you provide a deeper analysis of this matchup?')}`;

    await handleAddToChat(message);
    navigate('/chat');
  };

  if (!game) return null;

  const { teams, scores, game: gameInfo } = game;
  const isLive = ['Q1', 'Q2', 'Q3', 'Q4', 'HT', 'OT'].includes(gameInfo?.status?.short);
  const isFinished = ['FT', 'AOT'].includes(gameInfo?.status?.short);
  const isUpcoming = !isLive && !isFinished;
  const status = gameInfo?.status?.short;
  
  // Get display text for status badge
  const getStatusDisplay = () => {
    if (isLive) {
      const quarterMap = {
        'Q1': '1st',
        'Q2': '2nd', 
        'Q3': '3rd',
        'Q4': '4th',
        'HT': 'HT',
        'OT': 'OT'
      };
      const quarter = quarterMap[status] || t('LIVE');
      if (gameInfo?.status?.clock) {
        return `${quarter} ${gameInfo.status.clock}`;
      }
      return `${t('LIVE')} ${quarter}`;
    }
    if (isFinished) return t('FINAL');
    return null;
  };
  
  const statusDisplay = getStatusDisplay();
  const predictLabel = isFrozen
    ? 'COOLDOWN'
    : isSelected
    ? 'SELECTED'
    : isLive
    ? 'PREDICT LIVE'
    : 'PREDICT';
  const PredictIcon = isFrozen ? Clock : isSelected ? Check : Sparkles;
  
  // Format game time
  const formatGameTime = () => {
    if (isLive) return 'LIVE';
    if (isFinished) return 'Final';
    
    // Check for date and time fields (matching desktop structure)
    if (gameInfo?.date?.date && gameInfo?.date?.time) {
      try {
        // Combine date and time like desktop version does
        const dateTimeString = `${gameInfo.date.date}T${gameInfo.date.time}`;
        const gameDate = new Date(dateTimeString);
        
        if (!isNaN(gameDate.getTime())) {
          const options = { 
            hour: 'numeric', 
            minute: '2-digit', 
            hour12: true,
            timeZone: 'America/New_York'
          };
          return `${gameDate.toLocaleTimeString('en-US', options)} ET`;
        }
      } catch (e) {
        // Fall through to check alternative format
      }
    }
    
    // Fallback to direct date parsing if it exists
    if (gameInfo?.date && typeof gameInfo.date === 'string') {
      try {
        const gameDate = new Date(gameInfo.date);
        if (!isNaN(gameDate.getTime())) {
          const options = { 
            hour: 'numeric', 
            minute: '2-digit', 
            hour12: true,
            timeZone: 'America/New_York'
          };
          return `${gameDate.toLocaleTimeString('en-US', options)} ET`;
        }
      } catch (e) {
        // Fall through
      }
    }
    
    return 'TBD';
  };

  return (
    <div 
      className="relative border border-white/10 p-4 bg-white/[0.03]"
    >
      {/* Status Badge - Top Right */}
      {statusDisplay && (
        <div className="absolute top-2 right-2 z-10 mt-1">
          <div className={`flex items-center gap-1 px-2 py-0.5 rounded-full ${
            isLive ? 'bg-red-500' : 'bg-white/20'
          }`}>
            {isLive && <div className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" />}
            <span className="text-[10px] font-bold text-white">
              {statusDisplay}
            </span>
          </div>
        </div>
      )}
      
      {/* League/Week Label */}
      <div className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider mb-1.5 truncate pr-16">
        {gameInfo?.week ? (typeof gameInfo.week === 'string' && gameInfo.week.includes('Week') 
          ? gameInfo.week 
          : `Week ${gameInfo.week}`) : 'NFL'}
      </div>

      {/* Teams and Scores */}
      <div className="space-y-2 mt-3">

        {/* Away Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {teams?.away?.logo && (
              <img 
                src={teams.away.logo} 
                alt={teams.away.name}
                className="w-9 h-9 object-contain border border-white/10 rounded-sm bg-black/40"
                loading="lazy"
              />
            )}
            <span className="text-sm font-mono text-white tracking-tight leading-tight">
              {teams?.away?.name || 'Away Team'}
            </span>
          </div>
          <span className="text-xl font-black text-white ml-2 tabular-nums">
            {isFinished || isLive ? (scores?.away?.total ?? 0) : '-'}
          </span>
        </div>

        {/* Home Team */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            {teams?.home?.logo && (
              <img 
                src={teams.home.logo} 
                alt={teams.home.name}
                className="w-9 h-9 object-contain border border-white/10 rounded-sm bg-black/40"
                loading="lazy"
              />
            )}
            <span className="text-sm font-mono text-white tracking-tight leading-tight">
              {teams?.home?.name || 'Home Team'}
            </span>
          </div>
          <span className="text-xl font-black text-white ml-2 tabular-nums">
            {isFinished || isLive ? (scores?.home?.total ?? 0) : '-'}
          </span>
        </div>
      </div>

      {/* Game Time and Venue - only for upcoming games */}
      {!isFinished && !isLive && (
        <div className="mt-2 pt-1.5 border-t border-white/10">
          <div className="flex items-center justify-center gap-1 text-white/50">
            <Clock size={9} />
            <span className="text-[10px] font-medium">
              {formatGameTime()}
            </span>
            {gameInfo?.venue?.name && (
              <>
                <span className="text-white/30">•</span>
                <MapPin size={9} />
                <span className="text-[10px] font-medium truncate">
                  {gameInfo.venue.name}
                </span>
              </>
            )}
          </div>
        </div>
      )}

      {/* Prediction Result */}
      {prediction && (
        <div className="pt-1">
          <PredictionBox
            prediction={prediction}
            onAnalyze={handleAnalyzeInChat}
            variant="mobile"
            t={t}
          />
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-3 grid grid-cols-3 gap-2 items-stretch">
        {(isUpcoming || isLive) && (
          <div className="col-span-1">
            <XPicksButton
              gameData={{
                homeTeam: teams?.home?.name,
                awayTeam: teams?.away?.name,
                homeTeamId: teams?.home?.id,
                awayTeamId: teams?.away?.id,
                leagueId: game?.league?.id || 1,
                date: gameInfo?.date?.date || gameInfo?.date,
                time: formatGameTime(),
                gameId: gameInfo?.id
              }}
              sport="nfl"
              label="XPICKS"
            />
          </div>
        )}

        {showPredictionButton && (isUpcoming || isLive) && onToggleSelection && (
          <div className="col-span-1">
            <PredictButton
              label={predictLabel}
              icon={PredictIcon}
              isSelected={isSelected}
              isLive={isLive}
              isFrozen={isFrozen}
              onClick={(e) => {
                e.stopPropagation();
                if (!isFrozen) {
                  onToggleSelection(game);
                  if (onViewPrediction && !isSelected) {
                    onViewPrediction(game);
                  }
                }
              }}
            />
          </div>
        )}

        {/* Odds Button */}
        {onViewOdds && (
          <div className="col-span-1">
            <ViewOddsButton
              label="VIEW ODDS"
              onClick={() => onViewOdds(game)}
            />
          </div>
        )}
      </div>
    </div>
  );
});

NFLMobileGameCard.displayName = 'MobileNFLGameCard';

export default NFLMobileGameCard;
